import { useMemo, type ReactNode } from "react";
import { cx } from "../../../utils";

export enum CheckboxSize {
  SM = "SM", // 16px checkbox gap-2 text-sm
  BASE = "BASE" // 20px checkbox gap-3 text-base
}

export const Checkbox = ({
  label,
  subLabelText,
  checked,
  disabled,
  name,
  id,
  onChange,
  className,
  size = CheckboxSize.BASE,
  labelClassName,
  wrapperClassName,
  indeterminate,
  value,
  subLabelTextClassName,
  width = "full"
}: {
  label?: ReactNode;
  subLabelText?: ReactNode;
  checked?: boolean;
  id?: string;
  name?: string;
  disabled?: boolean;
  className?: string;
  labelClassName?: string;
  wrapperClassName?: string;
  indeterminate?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  size?: CheckboxSize;
  value?: string | number;
  subLabelTextClassName?: string;
  width?: "full" | "auto";
}) => {
  const sizeClasses = useMemo(() => {
    return {
      wrapper: size === CheckboxSize.SM ? "gap-2" : "gap-3",
      input: size === CheckboxSize.SM ? "h-4 w-4" : "h-5 w-5",
      label: size === CheckboxSize.SM ? "text-sm" : "text-base",
      subLabelText: size === CheckboxSize.SM ? "pl-6" : "pl-8"
    };
  }, [size]);

  return (
    <label
      className={cx(
        width === "auto" ? "w-auto" : "w-full",
        "transition-all",
        wrapperClassName
      )}
    >
      <div className={cx("flex w-full items-center", sizeClasses.wrapper)}>
        <input
          checked={checked}
          ref={(el) => {
            if (el) el.indeterminate = Boolean(indeterminate);
          }}
          className={cx(
            "shrink-0 text-primary-500 checked:disabled:bg-content-100 disabled:bg-background-900 rounded transition-all focus:ring-0 focus:ring-offset-0 disabled:cursor-not-allowed",
            sizeClasses.input,
            checked && !disabled ? "border-primary-500" : "border-content-100",
            className
          )}
          value={value}
          disabled={disabled}
          id={id}
          name={name}
          onChange={onChange}
          type="checkbox"
        />
        {label ? (
          <span
            className={cx(
              "truncate font-medium transition-all",
              sizeClasses.label,
              disabled ? "text-content-100" : "text-content-700",
              labelClassName
            )}
          >
            {label}
          </span>
        ) : null}
      </div>
      {subLabelText ? (
        <p
          className={cx(
            "text-xs text-content-400 mt-0.5",
            sizeClasses.subLabelText,
            subLabelTextClassName
          )}
        >
          {subLabelText}
        </p>
      ) : null}
    </label>
  );
};
